/**
 * Chart export quick-actions: to the clipboard, or to a file.
 * Part of the OPERATOR FEATURES set — see operatorFeatures.js for how to turn it off.
 *
 * <h2>Both capture the whole instrument, not a pane</h2>
 *
 * `getDataURL` renders the ECharts instance, and this chart is ONE instance with two grids — price
 * with the TrendPulse curve, and the indicator pane underneath. So the combined panes come out
 * together without anything having to stitch them, which is also why the two exports share a
 * capture function rather than each having their own idea of what "the chart" is.
 *
 * <p>At `pixelRatio: 2` the PNG is twice the on-screen resolution, which is what makes text in a
 * pasted chart legible rather than soft.
 */

import { dataUrlToBlob } from './chartSweepCapture';

export const EXPORT_PIXEL_RATIO = 2;

/** The chart as a PNG blob, or null if the instance cannot render one. */
export function captureChartBlob(chart, { pixelRatio = EXPORT_PIXEL_RATIO } = {}) {
  if (!chart || typeof chart.getDataURL !== 'function') return null;
  try {
    return dataUrlToBlob(chart.getDataURL({
      type: 'png', pixelRatio, backgroundColor: '#ffffff',
    }));
  } catch (err) {
    console.warn('[operator-export] capture failed', err);
    return null;
  }
}

/** `SYMBOL_TrendPulse_Manual_START_to_END.png`, as the study specifies it. */
export function screenshotFileName(symbol, startDate, endDate) {
  const clean = s => String(s || '').replace(/[^\w.-]/g, '');
  const sym = clean(symbol) || 'CHART';
  const from = clean(startDate);
  const to = clean(endDate);
  if (!from || !to) return null;
  return `${sym}_TrendPulse_Manual_${from}_to_${to}.png`;
}

/**
 * Is writing an image to the clipboard possible here?
 *
 * <p>Three things have to hold and all three fail quietly: the API has to exist, `ClipboardItem`
 * has to exist alongside it, and the page has to be in a secure context. Firefox shipped
 * `navigator.clipboard` long before it shipped image writes, so testing only the first would offer
 * a button that throws on click.
 */
export function canCopyImage() {
  return typeof navigator !== 'undefined'
    && !!navigator.clipboard
    && typeof navigator.clipboard.write === 'function'
    && typeof window !== 'undefined'
    && typeof window.ClipboardItem === 'function'
    && (window.isSecureContext !== false);
}

/**
 * Copy the chart to the system clipboard as a PNG.
 *
 * <p>Returns `{ok, reason}` rather than throwing, because every caller here is a button handler and
 * the interesting failures are ordinary rather than exceptional: a browser without image clipboard
 * support, and a page that has lost focus — Chrome rejects a clipboard write from a background
 * document, which is exactly what happens if the user clicks away while the capture is in flight.
 */
export async function copyChartToClipboard(chart, opts = {}) {
  if (!canCopyImage()) return { ok: false, reason: 'This browser cannot write images to the clipboard.' };
  const blob = captureChartBlob(chart, opts);
  if (!blob) return { ok: false, reason: 'Could not capture the chart.' };
  try {
    await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
    return { ok: true };
  } catch (err) {
    return {
      ok: false,
      reason: err && /focus/i.test(err.message || '')
        ? 'The page needs focus to write to the clipboard — click the chart and try again.'
        : `Clipboard write refused: ${err?.message || err}`,
    };
  }
}

/** Download the chart as a PNG under the study's filename. */
export function saveChartScreenshot(chart, { symbol, startDate, endDate, ...opts } = {}) {
  const name = screenshotFileName(symbol, startDate, endDate);
  if (!name) return { ok: false, reason: 'No date range to name the file with.' };
  const blob = captureChartBlob(chart, opts);
  if (!blob) return { ok: false, reason: 'Could not capture the chart.' };

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoked on a delay, not immediately: Chrome cancels a download whose blob URL is released
  // before it has finished reading it.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return { ok: true, name };
}
